import * as THREE from 'three';
import type { Textures } from '../engine/textures';

export type Materials = ReturnType<typeof createMaterials>;

/**
 * Bump mapping from one texture read: the height's own screen derivative (2×2 quad) instead of two extra
 * samples one pixel over. Same gradient magnitude, two fewer reads for every bumped pixel (floor, road, stone).
 */
THREE.ShaderChunk.bumpmap_pars_fragment = THREE.ShaderChunk.bumpmap_pars_fragment.replace(
  /vec2 dHdxy_fwd\(\) \{[\s\S]*?return vec2\( dBx, dBy \);\s*\}/,
  `vec2 dHdxy_fwd() {
		float H = bumpScale * texture2D( bumpMap, vBumpMapUv ).x;
		return vec2( dFdx( H ), dFdy( H ) );
	}`);

export function createMaterials(tex: Textures) {
  const std = (o: THREE.MeshStandardMaterialParameters) => new THREE.MeshStandardMaterial(o);
  return {
    basalt: std({ name: 'basalt', map: tex.stone.map, bumpMap: tex.stone.bump, bumpScale: 1.4, color: 0x8a837c, roughness: 0.58, metalness: 0.06 }),
    base: std({ name: 'baseStone', map: tex.stone.map, bumpMap: tex.stone.bump, bumpScale: 2, color: 0x625c57, roughness: 0.86 }),
    cold: std({ name: 'coldStone', map: tex.stone.map, bumpMap: tex.stone.bump, bumpScale: 1.4, color: 0x4a4744, roughness: 0.7 }),
    wall: std({ name: 'wall', map: tex.block.map, bumpMap: tex.block.bump, bumpScale: 3.2, color: 0x77706a, roughness: 0.9 }),
    cloth: std({ name: 'cloth', color: 0x3a0f0b, roughness: 0.95, side: THREE.DoubleSide }),
    clothDark: std({ name: 'clothDark', color: 0x1a0e0b, roughness: 1, side: THREE.DoubleSide }),
    well: std({ name: 'wellFloor', color: 0x0b0908, roughness: 0.5, metalness: 0.2, envMapIntensity: 0.5 }),
    groove: std({ name: 'groove', color: 0x040303, roughness: 1 }),
    bronze: std({ name: 'bronze', color: 0x8a5530, metalness: 0.95, roughness: 0.42 }),
    iron: std({ name: 'iron', color: 0x1e1b19, metalness: 0.85, roughness: 0.45 }),
    armor: std({ name: 'armor', color: 0x6a625c, metalness: 0.88, roughness: 0.32, envMapIntensity: 1.6 }),
    trim: std({ name: 'armorTrim', color: 0x9a7656, metalness: 0.95, roughness: 0.24, envMapIntensity: 1.6 }),
    leather: std({ name: 'leather', color: 0x17110d, roughness: 0.8 }),
    bossArmor: std({ name: 'bossArmor', color: 0x2e2624, metalness: 0.92, roughness: 0.3, envMapIntensity: 1.8 }),
    bossTrim: std({ name: 'bossTrim', color: 0xc2924e, metalness: 1, roughness: 0.2, envMapIntensity: 2 }),
    bossCloth: std({ name: 'bossCloth', color: 0x5a0c08, roughness: 0.9, side: THREE.DoubleSide }),
    eye: new THREE.MeshBasicMaterial({ name: 'eyeGlow', color: new THREE.Color(0xff3418).multiplyScalar(3) }),
    lava: new THREE.MeshBasicMaterial({ name: 'lava', color: new THREE.Color(0xff6a1c).multiplyScalar(2.6) }),
    pitGlow: new THREE.MeshBasicMaterial({ name: 'pitGlow', color: new THREE.Color(0xaf73ff).multiplyScalar(2.4) }),
    blob: new THREE.MeshBasicMaterial({ name: 'blob', map: tex.blob, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2 }),
  };
}

/** Environment lighting: a tiny procedural "room" baked into a PMREM cube. */
export function createEnvironment(renderer: THREE.WebGLRenderer): THREE.Texture {
  const pm = new THREE.PMREMGenerator(renderer), es = new THREE.Scene();
  es.add(new THREE.Mesh(new THREE.BoxGeometry(40, 40, 40), new THREE.MeshBasicMaterial({ color: 0x060505, side: THREE.BackSide })));
  const panel = (c: number, k: number, x: number, y: number, z: number, w: number, h: number) => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(c).multiplyScalar(k), side: THREE.DoubleSide }));
    m.position.set(x, y, z); m.lookAt(0, 0, 0); es.add(m);
  };
  panel(0x9fb2cc, 1.6, -9, 14, -8, 14, 7); panel(0xff6a28, 1.2, 9, 1, -6, 12, 3);
  panel(0xffa060, 1, 0, 3, 14, 16, 4); panel(0x6c7a90, 0.6, 10, 10, 6, 8, 6);
  const t = pm.fromScene(es, 0.04).texture;
  pm.dispose();
  return t;
}
